import React, { useState, useEffect, useRef } from 'react';
import styles from './EntitlementSearchResults.module.scss';
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  UseInfiniteHitsProps,
  useInstantSearch,
  useSearchBox,
  useStats,
} from 'react-instantsearch';
import { settings } from 'assets/settings';
import { ZAKAUT, grad1, grad2, leumitSearchClient } from 'assets/constants';
import strings from "assets/strings";
import { SERACH_DOCTORS_API } from 'assets/constants';
import Text from "components/text/Text";
import { EntitlementResult } from './EntitlementResult';
import type { BaseHit, Hit } from 'instantsearch.js';
import { EntitlementResultType } from 'types';
import CustomSearchBox from 'components/inputs/customSearchbox/CustomSearchBox';
import { Close, Arrow } from 'assets/icons';
import { getAllClassNames } from 'helpers';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { VirtualRefinementList } from 'components/virtualRefinement/VirtualRefinmentList';
import { useClickOutside } from 'hooks';
import Popup from 'components/popup/Popup';
import { AlertPopup } from 'components/popup/alertPopup/AlertPopup';

console.log("EntitlementSearchResults FILE LOADED", window.location.href);



const isSilverGoldHit = (hit: any) => {
  const z = hit?.zakauts;
  if (!z) return false;

  const hasSilver =
    Object.prototype.hasOwnProperty.call(z, "silver") && (z.silver?.length ?? 0) > 0;

  const hasGolden =
    Object.prototype.hasOwnProperty.call(z, "golden") && (z.golden?.length ?? 0) > 0;

  return hasSilver || hasGolden;
};

function InfiniteHitsComponent(
  props: UseInfiniteHitsProps & { onlySilverGold: boolean }
) {
  const { hits, showMore, isLastPage } = useInfiniteHits(props);

  const finalHits = props.onlySilverGold ? hits.filter(isSilverGoldHit) : hits;

  return (
    <div>
      {finalHits.map((hit: Hit<BaseHit>) => (
        <EntitlementResult key={hit.objectID} hit={hit as EntitlementResultType} />
      ))}

      {!props.onlySilverGold && !isLastPage && (
        <button
          className="ais-InfiniteHits-loadMore"
          onClick={showMore}
          disabled={isLastPage}
        >
          {strings.globalStrings.moreResults}
        </button>
      )}
    </div>
  );
}





const FieldsDropdown = ({ selectOptions, updateSelectedOption, onRemoveSelection }: { selectOptions: any[], updateSelectedOption: (val: string) => void, onRemoveSelection: () => void }) => {
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [items, setItems] = useState(selectOptions);
  const [selectedValue, setSelectedValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<SVGSVGElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  useClickOutside(dropdownRef, () => setShowDropdown(false));



  useEffect(() => {
    const updatedItems = items.map((option) => {
      if (option.value === selectedValue) {
        return {
          ...option,
          isSelected: true,
        };
      }
      return {
        ...option,
        isSelected: false,
      };
    });
    setItems(updatedItems);
  }, [selectedValue]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown]);


  const isSelected = (value: string) => {
    return value === selectedValue;
  }


  const handleRemoveSelectedOption = (): void => {
    setShowDropdown(false);
    const updatedItems = selectOptions.map((option) => {
      return {
        ...option,
        isSelected: false,
      };
    });
    setItems(updatedItems);
    setSelectedValue('');
    onRemoveSelection && onRemoveSelection();
  }

  const handleSelectedOption = (value: string): void => {
    setSelectedValue(value);
    setShowDropdown(false);
    updateSelectedOption(value);
  };

  // Function to set focus on a specific dropdown item
  const setFocus = (index: number) => {
    const dropdownItem = document.getElementById(`dropdown-item-${index}`);
    if (dropdownItem) {
      dropdownItem.focus(); // Set focus to the dropdown element
    }
  };


  const handleSpanKeyDown = (e: React.KeyboardEvent, item: any, index: number) => {    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleSelectedOption(item.value);
        closeRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (index + 1) % items.length;
        setHighlightedIndex(nextIndex);
        setFocus(nextIndex);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = (index - 1 + items.length) % items.length;
        setHighlightedIndex(prevIndex);
        setFocus(prevIndex);
        break;
    }
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<SVGSVGElement>, item: string) => {
    if (event.key === "Enter") {
      handleSelectedOption(item);
    }
  };

  return (
    <div>
      <div className={styles.divTop}>
        <label htmlFor='fieldService' id="dropdown-label">
          <Text className={styles.title} text={'תחום השירות'} />
        </label>
      </div>
      <div>
        <div
          className={getAllClassNames([
            styles.dropdownContainer,
          ])}
          onClick={(e) => {
            // Only toggle dropdown if the container itself is clicked
            if (e.target === e.currentTarget) {
              setShowDropdown((prevState) => !prevState); // Toggle between true and false
            }
          }}
        >
          {selectOptions && selectOptions
            .filter((item) => item.isSelected)
            .map((item) => (
              <li
                key={item.value}
                className={getAllClassNames([
                  styles.item,
                  "ais-RefinementList-item",
                  //isSelected(item.value) && "ais-RefinementList-item--selected",
                ])}
              >
                <span
                  className={getAllClassNames([

                    styles.label,
                  ])}
                >
                  {item.label}
                </span>
                <Close
                  onClick={() => handleRemoveSelectedOption()}
                  className={styles.icon}
                />
              </li>
            ))}
          {items && items
            .filter((item) => item.isSelected)
            .map((item, index) => (
              <li
                key={item.value}
                className={getAllClassNames([
                  styles.item,
                  "ais-RefinementList-item",
                  isSelected(item.value) && "ais-RefinementList-item--selected",
                ])}
              >
                <span
                  className={`dropdown-item-${index} ${getAllClassNames([
                    "ais-RefinementList-labelText",
                    styles.label,
                  ])}`}
                >
                  {item.label}
                </span>
                <Close
                  ref={closeRef}
                  onClick={() => handleRemoveSelectedOption()}
                  className={styles.icon}
                  aria-label={`${strings.globalStrings.remove} ${item.label}`} // Added aria-label for accessibility
                  role="button" // Added role for screen readers
                  tabIndex={0} // Make the element focusable
                  onKeyDown={(event) => handleCloseKeyDown(event, item)}
                />
              </li>
            ))}
          <button id="fieldService"
            aria-expanded={showDropdown}
            aria-label={showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand}
            onClick={() => setShowDropdown((prevState) => !prevState)}
            className={styles.iconArrow} aria-labelledby="dropdown-label">
            <SvgGenerator
              className={styles.svg}
              name="Arrow"
              objectName="zakautSearchResults"
            />
          </button>
        </div>
        {showDropdown && (
          <div ref={dropdownRef} className={styles.dropdown}>
            <Arrow
              onClick={() => setShowDropdown((prevState) => !prevState)}
              className={styles.dropDownIconArrow}
              aria-label={`${showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand} ${strings.entitlementSearch.fieldService}`} // Added aria-label for accessibility`}
            />
            <ul className={styles.list} role="listbox" aria-labelledby="dropdown-label"
              aria-activedescendant={
                highlightedIndex !== null
                  ? `dropdown-item-${highlightedIndex}`
                  : undefined
              }>
              {items.map((item, index) => (
                <li
                  role="option"
                  key={item.value}
                  className={getAllClassNames([
                    "ais-RefinementList-item",
                    isSelected(item.value) && "ais-RefinementList-item--selected",
                  ])}
                >
                  <label
                    onClick={() => handleSelectedOption(item.value)}
                    className="ais-RefinementList-label"
                    id={`dropdown-item-${index}`} // Add an ID to each item
                    role="option" // Added role for screen readers
                    tabIndex={0} // Make the element focusable
                    aria-selected={highlightedIndex === index}
                    onKeyDown={(e) => handleSpanKeyDown(e, item, index)}
                  >
                    <span
                      className={getAllClassNames([
                        "ais-RefinementList-labelText",
                        styles.textMiddle,
                      ])}
                    //tabIndex={0} 
                    >
                      {item.label}

                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className={styles.height} />
          </div>
        )}
      </div>
    </div>
  );
}


export function EntitlementSearchResults() {
  const [fieldOptions, setFieldOptions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedField, setSelectedField] = React.useState('');
  //const [hasSearched, setHasSearched] = React.useState(false); // Track if a search has been initiated
  //const [fromSubmit, setFromSubmit] = React.useState(false); // Track if results should always be shown
  //const [shouldShowResults, setShouldShowResults] = React.useState(false); // Immediate render control
  const [showPopup, setShowPopup] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  console.log("EntitlementSearchResults RENDER", window.location.search);

  const [onlySilverGold, setOnlySilverGold] = useState(false);



  useEffect(() => {
    const readKey = () => {
      const search = window.location.search;
      if (search) {
        return new URLSearchParams(search).get("key");
      }
  
      const hash = window.location.hash;
      const qIndex = hash.indexOf("?");
      if (qIndex >= 0) {
        return new URLSearchParams(hash.substring(qIndex)).get("key");
      }
  
      return null;
    };
  
    let tries = 0;
  
    const tick = () => {
      const key = readKey();
      const isSilverGold = key?.toLowerCase() === "silvergold";
  
      setOnlySilverGold(isSilverGold);
  
      console.log("[key-check]", {
        tries,
        href: window.location.href,
        search: window.location.search,
        hash: window.location.hash,
        key,
        isSilverGold,
      });
  
      if (!key && tries < 20) {
        tries += 1;
        setTimeout(tick, 100);
      }
    };
  
    tick();
  
    const onUrlChange = () => tick();
    window.addEventListener("popstate", onUrlChange);
    window.addEventListener("hashchange", onUrlChange);
  
    return () => {
      window.removeEventListener("popstate", onUrlChange);
      window.removeEventListener("hashchange", onUrlChange);
    };
  }, []);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const customStyle = windowWidth > 992
    ? { width: 'calc(100% - 225px)', insetInlineStart: '66px' }
    : { width: '100%' };

  const handleSelectedOption = (value: string): void => {
    setSelectedField(value);
    //setHasSearched(true); // Mark search as initiated when a field is selected
  };

  const handleRemoveSelectedOption = (): void => {
    setSelectedField('');
    //setHasSearched(true); // Mark search as initiated even if the field is removed
  };

  useEffect(() => {  
    setSearchedQuery(searchedQuery);
  }, [searchedQuery]);

  // const handleSearchSubmit = (): void => {
  //   setHasSearched(true); // Ensure results are shown when submitted
  // };

  // useEffect(() => {
  //   // Trigger an update to shouldShowResults when fromSubmit or search conditions change
  //   //setShouldShowResults((searchedQuery !== '' || hasSearched) || selectedField !== '');
  // }, [searchedQuery, hasSearched, selectedField]);

  useEffect(() => {
    const fetchOptions = async () => {
      const response = await fetch(
        `https://${settings.searchApiUrl}${SERACH_DOCTORS_API}zakaut/fields`
      );
      const options: any[] = await response.json();
      const normalizedOptions = options.map((item) => {
        return {
          value: item.name,
          label: item.name,
          isSelected: false,
        };
      }).sort((a, b) => a.label.localeCompare(b.label)); // Sort the options
      setFieldOptions(normalizedOptions);
      setLoading(false);
    };
    fetchOptions();
  }, []);

  const Stats = () => {
    const { nbHits } = useStats();
    return nbHits ? (
      <div className={styles.resultsCount}>
        <Text
          className={styles.title}
          text={`${strings.entitlementSearch.found} ${nbHits} ${selectedField
              ? `${strings.entitlementSearch.entitlements} ${selectedField}`
              : `${strings.entitlementSearch.entitlemenFreeText} '${searchedQuery}'`
            }`}

        />
      </div>
    ) : null;
  };

  const HitsWrapper = () => {
    //const { nbHits } = useStats();
    const { query } = useSearchBox();
    const { status } = useInstantSearch();
    const { hits } = useInfiniteHits();
    const filteredHits = onlySilverGold ? hits.filter(isSilverGoldHit) : hits;
    console.log('hits:', hits);
    console.log('filteredHits:', filteredHits);
    const hasAnyHits = filteredHits.length > 0;
    console.log('hasAnyHits:', hasAnyHits);

    const noResultsRef = useRef<HTMLDivElement>(null);
    const noResultsRef2 = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (status === 'idle' && !hasAnyHits && noResultsRef.current) {
        noResultsRef.current.focus();
      }
      if (status === 'idle' && !hasAnyHits && noResultsRef2.current) {
        noResultsRef2.current.focus();
      }
    }, [status, hasAnyHits]);
    

    return hasAnyHits ? (
      <InfiniteHitsComponent onlySilverGold={onlySilverGold} />
      ) : (
        (selectedField) && !hasAnyHits && status === 'idle'
        ? (
        <div
          className={styles.noMessagesWrapper}
          role="status"
          aria-live="assertive"
          tabIndex={-1}
          ref={noResultsRef}
        >
          <SvgGenerator
            name="Magnifier"
            className="magnifierIcon"
            objectName="medicalCenterSearchResults"
          />
          <Text text={strings.globalStrings.noResultsFound} />
        </div>
      ) : (query && !hasAnyHits && status === 'idle' ? <div className={styles.noMessagesWrapper} role="status" aria-live="assertive" tabIndex={-1} ref={noResultsRef2}>
        <SvgGenerator
          name="Magnifier"
          className="magnifierIcon"
          objectName="medicalCenterSearchResults"
        />
        <Text text={strings.globalStrings.noResultsFound} style={{ textAlign: 'center' }} />
      </div> : null)
    );
  };

  return (
    <div className="row ais-InstantSearch">
      <InstantSearch indexName={ZAKAUT} searchClient={leumitSearchClient}>
        <Configure
          //restrictSearchableAttributes={['serviceName']}
          snippetEllipsisText="…"
          hitsPerPage={1000}
          filters={selectedField ? `fieldsNameList:"${selectedField}"` : ''}
        />
        {!loading && (
          <div className="col-12 ais-SearchResults">
            <div className={styles.searchDiv}>
              <Text
                text={strings.entitlementSearch.searchTitle}
                className={styles.serachTitle}
              />
              <div className={styles.searchFields}>
                <div
                  className={getAllClassNames([
                    styles.fieldsDropDown,
                    styles.searchBox,
                  ])}
                >
                  <FieldsDropdown
                    selectOptions={fieldOptions}
                    updateSelectedOption={(value: string) =>
                      handleSelectedOption(value)
                    }
                    onRemoveSelection={handleRemoveSelectedOption}
                  />
                </div>
                <div className={styles.searchBox}>
                  <CustomSearchBox
                    enableOnlyAlphanumeric
                    color1={grad1}
                    color2={grad2}
                    btnText={strings.globalStrings.search}
                    label="חיפוש חופשי"
                    indexName={ZAKAUT}
                    isAutoComplete
                    //onSubmit={() => setHasSearched(true)} // Track submission
                    onEmptySubmit={(isEmpty: boolean) => { isEmpty && !selectedField ? setShowPopup(isEmpty) : null }} // Show popup if search is empty
                    onGetQuery={(query: string) => { query.length > 0 ? setSearchedQuery(query) : setSearchedQuery('') }} // Track if a search has been initiated
                    restrictSearchableAttributes={['serviceName']}
                    // add style prop with calc(100% - 217px) to the input
                    //add important to the font weight in the label
                    customLabelClass='font-weight-bold'
                    style={customStyle}
                  />
                </div>
              </div>
            </div>
            {!onlySilverGold && <Stats />}
            <HitsWrapper />
            <VirtualRefinementList attribute="fieldsNameList" />
          </div>
        )}
        {showPopup &&
          <Popup
            onClose={() => setShowPopup(false)}
            height={284}
            width={550}
            style={{ paddingBottom: '0px' }}
          >
            <AlertPopup
              title={strings.errorMessages.notice}
              subTitle={strings.errorMessages.freeTextSearchRequired}
              showButton
              buttonText={strings.globalStrings.close}
              onClose={() => setShowPopup(false)}
            ></AlertPopup>
          </Popup>
        }
      </InstantSearch>
    </div>
  );
}


